import { connect } from "react-redux";
import React, { useRef, useState, useEffect } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { v4 as uuidv4 } from 'uuid';
import { mockupCreate } from 'actions/mockupAction';

const PRODUCT_OPTIONS = {
    "T-SHIRT": ["BLACK", "WHITE", "SPORT GREY", "SAND", "FOREST GREEN", "LIGHT PINK", "NAVY", "MILITARY GREEN", "MAROON", "RED", "ROYAL BLUE", "ASH GREY", "LIGHT BLUE", "CHARCOAL", "DARK HEATHER", "PURPLE", "ORANGE"],
    "YOUTH T-SHIRT": ["BLACK", "WHITE", "IRISH GREEN", "LIGHT PINK", "FOREST GREEN"],
    "SWEATSHIRT": ["BLACK", "WHITE", "SPORT GREY", "SAND", "FOREST GREEN", "LIGHT PINK", "NAVY", "MILITARY GREEN", "MAROON", "RED", "ROYAL BLUE", "ASH GREY", "ORANGE", "DARK HEATHER"],
    "HOODIE": ["BLACK", "WHITE", "SPORT GREY", "SAND", "FOREST GREEN", "LIGHT PINK", "NAVY"],
    "TANK TOP": ["BLACK", "WHITE", "SPORT GREY", "NAVY"],
    "COMFORT COLOR": ["BLACK", "WHITE", "PEPPER", "IVORY", "ESPRESSO", "BLUE SPRUCE", "CRIMSOM", "BAY", "MOSS", "BLUE JEAN", "SEAFOAM", "GREY", "BLOSSOM", "GRAPHITE", "VIOLET"],
    "WASHED": ["BLACK", "GREY"]
};

const COLOR_HEX = {
    "BLACK": "#000000", "WHITE": "#FFFFFF", "SPORT GREY": "#C0C0C0", "SAND": "#F5DEB3", "FOREST GREEN": "#228B22",
    "LIGHT PINK": "#FFB6C1", "NAVY": "#000080", "MILITARY GREEN": "#4B5320", "MAROON": "#800000", "RED": "#FF0000",
    "ROYAL BLUE": "#4169E1", "ASH GREY": "#B2BEB5", "LIGHT BLUE": "#ADD8E6", "CHARCOAL": "#36454F",
    "DARK HEATHER": "#A9A9A9", "PURPLE": "#800080", "ORANGE": "#FFA500", "IRISH GREEN": "#00A550",
    "PEPPER": "#3C3C3C", "IVORY": "#FFFFF0", "ESPRESSO": "#4B2E2B", "BLUE SPRUCE": "#317873", "CRIMSOM": "#DC143C",
    "BAY": "#A3C1AD", "MOSS": "#8A9A5B", "BLUE JEAN": "#5D8AA8", "SEAFOAM": "#93E9BE", "GREY": "#808080",
    "BLOSSOM": "#FFD3E0", "GRAPHITE": "#383838", "VIOLET": "#8F00FF"
};

const ZONE_COLORS = {
    print: new THREE.Color('#00ff00'), // xanh lá
    delete: new THREE.Color('#ff0000') // đỏ
};

const ImageEditor3D = ({ mockupCreate }) => {
    const cropperRef = useRef(null);
    const threeCanvasRef = useRef(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [fixedSize, setFixedSize] = useState({ width: 0, height: 0 });
    const [zones, setZones] = useState([]);
    const [productType, setProductType] = useState("");
    const [color, setColor] = useState("");
    const [imageUrl, setImageUrl] = useState("");

    const sceneRef = useRef();
    const cameraRef = useRef();
    const rendererRef = useRef();
    const meshMap = useRef({});

    const finalCanvasRef = useRef(null);
    const imageRef = useRef(null);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => setImageSrc(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleImageUrlChange = (e) => {
        setImageUrl(e.target.value);
    };

    const loadImageFromUrl = () => {
        setImageSrc(imageUrl);
    };

    const getCroppedImage = () => {
        const cropper = cropperRef.current?.cropper;
        const canvas = cropper.getCroppedCanvas({ maxWidth: 1000, maxHeight: 1000, fillColor: '#fff' });
        const ratio = Math.min(1000 / canvas.width, 1000 / canvas.height);
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = canvas.width * ratio;
        finalCanvas.height = canvas.height * ratio;
        const ctx = finalCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, finalCanvas.width, finalCanvas.height);
        const url = finalCanvas.toDataURL();
        setCroppedImage(url);
        setFixedSize({ width: finalCanvas.width, height: finalCanvas.height });
    };

    const addZone = (type, id) => {
        const safeType = type === 'delete' ? 'delete' : 'print';
        if (id) {
            const newZone = zones.find(z => z.id === id);
            if (newZone) {
                setZones(prev => [...prev, {
                    ...newZone,
                    id: uuidv4(),
                    type: safeType,
                    posZ: newZone.posZ + 10,
                    width: newZone.width - 30,
                    height: newZone.height - 30,
                }]);
                return;
            }
        }
        setZones(prev => [...prev, {
            id: uuidv4(),
            type: safeType,
            width: 200,
            height: 200,
            shape: 'rectangle',
            rotateX: 0, rotateY: 0, rotateZ: 0,
            posX: 0, posY: 0, posZ: 0,
            warpX: 0, warpY: 0,
            watermark: { text: "COTIK", opacity: 0.5, fontSize: 24, position: { x: 20, y: 20 } }
        }]);
    };

    const updateZone = (id, newData) => {
        if (newData.rotateX !== undefined) newData.rotateX = Math.max(-90, Math.min(90, newData.rotateX));
        if (newData.rotateY !== undefined) newData.rotateY = Math.max(-90, Math.min(90, newData.rotateY));
        if (newData.rotateZ !== undefined) newData.rotateZ = Math.max(-90, Math.min(90, newData.rotateZ));
        setZones(prev => prev.map(z => z.id === id ? { ...z, ...newData } : z));
    };

    const deleteZone = (id) => {
        setZones(prev => prev.filter(z => z.id !== id));
    };

    useEffect(() => {
        if (!croppedImage || !fixedSize.width) return;
        const canvas = threeCanvasRef.current;
        const renderer = new THREE.WebGLRenderer({ canvas, alpha: true });
        renderer.setSize(fixedSize.width, fixedSize.height);
        rendererRef.current = renderer;

        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(45, fixedSize.width / fixedSize.height, 0.1, 2000);
        camera.position.z = 400;
        cameraRef.current = camera;

        new OrbitControls(camera, renderer.domElement);

        scene.add(new THREE.AmbientLight(0xffffff, 1.2));
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
        directionalLight.position.set(100, 100, 100);
        scene.add(directionalLight);

        renderer.render(scene, camera);

        return () => {
            renderer.dispose();
        };
    }, [croppedImage, fixedSize]);

    useEffect(() => {
        if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;
        const scene = sceneRef.current;
        const camera = cameraRef.current;
        const renderer = rendererRef.current;

        Object.values(meshMap.current).forEach(m => {
            m.geometry?.dispose();
            m.material?.dispose();
            scene.remove(m);
        });
        meshMap.current = {};

        const orderedZones = [...zones.filter(z => z.type === 'print'), ...zones.filter(z => z.type === 'delete')];

        orderedZones.forEach((zone) => {
            let geometry;

            if (zone.shape === 'circle') {
                const radius = Math.min(zone.width, zone.height) / 2;
                geometry = new THREE.CircleGeometry(radius, 64);
            } else if (zone.shape === 'triangle') {
                const w = zone.width / 2;
                const h = zone.height / 2;
                const vertices = new Float32Array([
                    0, h, 0,     // đỉnh trên
                    -w, -h, 0,   // góc trái
                    w, -h, 0     // góc phải
                ]);
                geometry = new THREE.BufferGeometry();
                geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
                geometry.computeVertexNormals();
            } else if (zone.shape === 'ellipse') {
                const radiusX = zone.width / 2;
                const radiusY = zone.height / 2;

                const curve = new THREE.EllipseCurve(
                    0, 0,             // center x, y
                    radiusX, radiusY, // xRadius, yRadius
                    0, 2 * Math.PI,   // start angle, end angle
                    false,            // clockwise
                    0                 // rotation
                );

                const points = curve.getPoints(64);
                const shape = new THREE.Shape(points);
                geometry = new THREE.ShapeGeometry(shape);
            } else {
                geometry = new THREE.PlaneGeometry(zone.width, zone.height, 50, 50);
            }

            if (zone.type !== 'delete') {
                const pos = geometry.attributes.position;
                const ampX = zone.warpX || 0, ampY = zone.warpY || 0;
                for (let i = 0; i < pos.count; i++) {
                    const x = pos.getX(i), y = pos.getY(i);
                    const wave = Math.sin(x * 0.1) * ampX + Math.cos(y * 0.1) * ampY;
                    pos.setZ(i, wave);
                }
                pos.needsUpdate = true;
                geometry.computeVertexNormals();
            }

            const material = new THREE.MeshStandardMaterial({
                color: ZONE_COLORS[zone.type] || new THREE.Color('#0000ff'),
                transparent: true,
                opacity: 0.9,
                roughness: 0.7,
                metalness: 0.1,
                side: THREE.DoubleSide,
            });

            const plane = new THREE.Mesh(geometry, material);
            plane.rotation.set(
                THREE.MathUtils.degToRad(zone.rotateX),
                THREE.MathUtils.degToRad(zone.rotateY),
                THREE.MathUtils.degToRad(zone.rotateZ)
            );
            plane.position.set(zone.posX, zone.posY, zone.posZ);
            plane.renderOrder = zone.type === 'delete' ? 2 : 1;

            scene.add(plane);
            meshMap.current[zone.id] = plane;
        });

        renderer.render(scene, camera);

        const finalCanvas = finalCanvasRef.current;
        const ctx = finalCanvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);
            ctx.drawImage(imageRef.current, 0, 0);
            ctx.drawImage(renderer.domElement, 0, 0);
        }
    }, [zones]);

    const generateImageWith3D = async () => {
        return new Promise((resolve) => {
            finalCanvasRef.current.toBlob(resolve, "image/png");
        });
    };

    const handleSave = async () => {
        if (!croppedImage || !productType || !color || zones.length === 0) return;
        const croppedBlob = await fetch(croppedImage).then(res => res.blob());
        const blobWithZones = await generateImageWith3D();
        const formData = new FormData();
        formData.append('image', croppedBlob, 'cropped.png');
        formData.append('imageWithZones', blobWithZones, 'preview.png');
        formData.append('zones', JSON.stringify(zones));
        formData.append('productType', productType);
        formData.append('color', color);
        formData.append('size', JSON.stringify(fixedSize));
        await mockupCreate(formData);
    };



    return (
        <div className="">
            <div className="row">
                <div className="col-12">
                    <h3 className="mt-4">Create mockup</h3>
                </div>
            </div>
            <div className="row">
                {!croppedImage && (
                    <div className="col">
                        <div className="mb-3">
                            <label htmlFor="imageUrl" className="form-label">URL Image</label>
                            <input
                                type="text"
                                id="imageUrl"
                                className="form-control"
                                placeholder="URL Image"
                                value={imageUrl}
                                onChange={handleImageUrlChange}
                            />
                        </div>
                        <button className="btn btn-info mb-3" onClick={loadImageFromUrl}>Load URL</button>
                        <div className="mb-3">
                            <label htmlFor="imageFile" className="form-label">Or Select file</label>
                            <input
                                type="file"
                                className="form-control"
                                onChange={handleImageChange}
                            />
                        </div>

                        <div>
                            <Cropper
                                src={imageSrc}
                                ref={cropperRef}
                                style={{ height: 400, width: '100%' }}
                                aspectRatio={0}
                                guides={true}
                                viewMode={1}
                            />
                            {imageSrc && <button className="btn btn-primary mt-3" onClick={getCroppedImage}>Crop</button>}

                        </div>

                    </div>
                )}
                {croppedImage && (
                    <>
                        <div className="col">
                            <div style={{ position: 'relative', width: fixedSize.width, height: fixedSize.height }}>
                                <img ref={imageRef} src={croppedImage} style={{ width: '100%', height: '100%' }} alt="Cropped" />
                                <canvas ref={threeCanvasRef} style={{ position: 'absolute', top: 0, left: 0 }} />
                            </div>
                        </div>
                    </>
                )}
                {croppedImage && (
                    <>
                        <div className="col">
                            <div className="d-flex align-items-center">
                                <button className="btn btn-primary mt-3 me-5" onClick={() => addZone('print')}>Add Print Area</button>
                                <span>Should be adjusted using the parameters below</span>
                            </div>
                            <div className="d-flex gap-2 mt-3">
                                {zones.map((z, i) => (
                                    <div key={z.id} style={{ marginTop: 10, border: "1px solid #cecece", padding: 10, borderRadius: 5 }}>
                                        <strong>{z.type === 'delete' ? `Delete Area #${i + 1}` : `Print Area #${i + 1}`}</strong>
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: `${z.watermark?.position?.y || 0}px`,
                                                left: `${z.watermark?.position?.x || 0}px`,
                                                opacity: z.watermark?.opacity || 1,  // Set opacity to make watermark visible
                                                fontSize: `${z.watermark?.fontSize || 24}px`,
                                                color: '#ffffff',
                                                fontFamily: 'Arial, sans-serif',
                                                pointerEvents: 'none',
                                                zIndex: 100,  // Make sure watermark is on top
                                                userSelect: 'none',
                                            }}
                                        >
                                            {z.watermark?.text || "COTIK"}
                                        </div>
                                        <div className="form-group">
                                            <label>Zone type</label>
                                            <select className="form-control" onChange={(e) => updateZone(z.id, { shape: e.target.value })}>
                                                <option value="rectangle">Rectangle</option>
                                                <option value="circle">Circle</option>
                                                <option value="triangle">Triangle</option>
                                                <option value="ellipse">Ellipse</option>
                                            </select>
                                        </div>

                                        {["X", "Y", "Z"].map(axis => (
                                            <div key={axis}>
                                                Rotate {axis}:{' '}
                                                <input
                                                    type="number"
                                                    value={z[`rotate${axis}`]}
                                                    onChange={e => updateZone(z.id, { [`rotate${axis}`]: parseInt(e.target.value) })}
                                                    style={{ width: '60px', marginRight: '10px' }}
                                                />
                                                <br></br>
                                                <input
                                                    type="range"
                                                    min={-90}
                                                    max={90}
                                                    value={z[`rotate${axis}`]}
                                                    onChange={e => updateZone(z.id, { [`rotate${axis}`]: parseInt(e.target.value) })}
                                                    style={{ width: '400px' }}
                                                />
                                            </div>
                                        ))}
                                        <div>
                                            Position X:

                                            <input
                                                type="number"
                                                value={z.posX}
                                                min={-300}
                                                max={300}
                                                onChange={e => updateZone(z.id, { posX: parseInt(e.target.value) })}
                                                style={{ width: '60px', marginRight: '10px' }}
                                            />
                                            <br></br>
                                            <input
                                                type="range"
                                                min={-300}
                                                max={300}
                                                value={z.posX}
                                                style={{ width: '400px' }}
                                                onChange={e => updateZone(z.id, { posX: parseInt(e.target.value) })}
                                            />
                                        </div>
                                        <div>
                                            Position Y:

                                            <input
                                                type="number"
                                                value={z.posY}
                                                min={-300}
                                                max={300}
                                                onChange={e => updateZone(z.id, { posY: parseInt(e.target.value) })}
                                                style={{ width: '60px', marginRight: '10px' }}
                                            />
                                            <br></br>
                                            <input
                                                type="range"
                                                min={-300}
                                                max={300}
                                                value={z.posY}
                                                style={{ width: '400px' }}
                                                onChange={e => updateZone(z.id, { posY: parseInt(e.target.value) })}
                                            />
                                        </div>
                                        <div>
                                            Position Z:

                                            <input
                                                type="number"
                                                value={z.posZ}
                                                min={-300}
                                                max={300}
                                                onChange={e => updateZone(z.id, { posZ: parseInt(e.target.value) })}
                                                style={{ width: '60px', marginRight: '10px' }}
                                            />
                                            <br></br>
                                            <input
                                                type="range"
                                                min={-300}
                                                max={300}
                                                value={z.posZ}
                                                style={{ width: '400px' }}
                                                onChange={e => updateZone(z.id, { posZ: parseInt(e.target.value) })}
                                            />
                                        </div>
                                        <div>
                                            Wave X:
                                            <input
                                                type="number"
                                                value={z.warpX}
                                                step={0.5}
                                                min={-3}
                                                max={3}
                                                onChange={e => updateZone(z.id, { warpX: parseInt(e.target.value) })}
                                                style={{ width: '60px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                step={0.5}
                                                min={-3}
                                                max={3}
                                                value={z.warpX}
                                                onChange={e => updateZone(z.id, { warpX: parseInt(e.target.value) })}
                                                style={{ width: '400px' }}
                                            />
                                        </div>
                                        <div>
                                            Wave Y:
                                            <input
                                                type="number"
                                                step={0.5}
                                                value={z.warpY}
                                                min={-3}
                                                max={3}
                                                onChange={e => updateZone(z.id, { warpY: parseInt(e.target.value) })}
                                                style={{ width: '60px', marginRight: '10px' }}
                                            />
                                            <br />
                                            <input
                                                type="range"
                                                step={0.5}
                                                min={-3}
                                                max={3}
                                                value={z.warpY}
                                                onChange={e => updateZone(z.id, { warpY: parseInt(e.target.value) })}
                                                style={{ width: '400px' }}
                                            />
                                        </div>

                                        <div>
                                            Width:
                                            <input
                                                type="number"
                                                value={z.width}
                                                min={50}
                                                max={500}
                                                onChange={e => updateZone(z.id, { width: parseInt(e.target.value) })}
                                                style={{ width: '60px', marginRight: '10px' }}
                                            />
                                            <br></br>
                                            <input
                                                type="range"
                                                min={50}
                                                max={500}
                                                value={z.width}
                                                onChange={e => updateZone(z.id, { width: parseInt(e.target.value) })}
                                                style={{ width: '400px' }}
                                            />
                                        </div>
                                        <div>
                                            Height:
                                            <input
                                                type="number"
                                                value={z.height}
                                                min={50}
                                                max={500}
                                                onChange={e => updateZone(z.id, { height: parseInt(e.target.value) })}
                                                style={{ width: '60px', marginRight: '10px' }}
                                            />
                                            <br></br>
                                            <input
                                                type="range"
                                                min={50}
                                                max={500}
                                                value={z.height}
                                                onChange={e => updateZone(z.id, { height: parseInt(e.target.value) })}
                                                style={{ width: '400px' }}
                                            />
                                        </div>
                                        <button className="btn btn-sm btn-warning" onClick={() => addZone('delete', z.id)}>Add Delete Print Area</button>
                                        <button className="btn btn-sm btn-danger" onClick={() => deleteZone(z.id)}>Clear Print Area</button>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-3">
                                <label className="form-label">Select Product</label>
                                <select className="form-select" value={productType} onChange={e => setProductType(e.target.value)}>
                                    <option value="">-- Select Product --</option>
                                    {Object.keys(PRODUCT_OPTIONS).map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            {productType && (
                                <div className="mt-3">
                                    <label className="form-label">Select Color</label>
                                    <select className="form-select" value={color} onChange={e => setColor(e.target.value)}>
                                        <option value="">-- Select Color --</option>
                                        {PRODUCT_OPTIONS[productType].map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {color && (
                                <div className="mt-3" style={{ width: 30, height: 30, backgroundColor: COLOR_HEX[color], border: '1px solid #000' }}></div>
                            )}

                            <div className="mt-3">
                                <button className="btn btn-success" onClick={handleSave}>Save To Database</button>
                            </div>
                        </div>

                    </>
                )}
            </div>
            <canvas ref={finalCanvasRef} width={fixedSize.width} height={fixedSize.height} style={{ display: 'none' }} />
        </div>
    );
};
const mapStateToProps = (state) => ({});
const mapDispatchToProps = { mockupCreate };
export default connect(mapStateToProps, mapDispatchToProps)(ImageEditor3D);
